import { useMemo } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Users, FileText, Euro, Zap, Flame, TrendingUp, TrendingDown,
  Clock, FileSignature, UserCheck, BarChart3, Activity,
  ChevronRight, Plus, Search, Receipt, ArrowUpRight
} from "lucide-react";

const COLORS = {
  green: "#006838",
  neon: "#ADFF2F",
  blue: "#3b82f6",
  purple: "#8b5cf6",
  orange: "#f97316",
  yellow: "#eab308",
  red: "#ef4444",
  emerald: "#10b981",
  teal: "#14b8a6",
};

function MetricCard({ title, value, subtitle, icon: Icon, color, borderColor, trend, onClick }) {
  return (
    <Card className={`bg-white hover:shadow-lg transition-all cursor-pointer border-l-4 ${borderColor}`} onClick={onClick}
      data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{title}</p>
            <h3 className={`text-2xl font-black mt-1 ${color}`}>{value}</h3>
            {subtitle && (
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                {trend === "up" && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                {trend === "down" && <TrendingDown className="w-3 h-3 text-red-500" />}
                {subtitle}
              </p>
            )}
          </div>
          {Icon && (
            <div className={`p-2.5 rounded-xl bg-opacity-10`} style={{ backgroundColor: `${COLORS[color?.replace('text-', '') || 'green']}15` }}>
              <Icon className="w-5 h-5" style={{ color: COLORS[color?.replace('text-', '') || 'green'] }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({ label, value, color, isLast, pct }) {
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className={`flex-1 p-3 rounded-xl text-center ${color}`}>
        <p className="text-2xl font-black">{value}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wider mt-0.5">{label}</p>
        {pct !== undefined && <p className="text-[9px] mt-0.5 opacity-75">{pct}%</p>}
      </div>
      {!isLast && <ChevronRight className="w-5 h-5 text-gray-300 shrink-0" />}
    </div>
  );
}

export default function AdvancedDashboard({ stats, marketPrices, precheckHistory, onTabChange }) {
  const trendData = useMemo(() => stats?.trend_mensile || [], [stats]);

  const pieData = useMemo(() => [
    { name: "Luce", value: stats?.analisi?.luce || 0, color: COLORS.yellow },
    { name: "Gas", value: stats?.analisi?.gas || 0, color: COLORS.orange },
  ], [stats]);

  const ratingData = useMemo(() => [
    { name: "Promosse", value: stats?.analisi?.promosse || 0, color: COLORS.emerald },
    { name: "Bocciate", value: stats?.analisi?.bocciate || 0, color: COLORS.red },
  ], [stats]);

  if (!stats) return null;

  const activityIcons = { contratto: FileSignature };
  const activityColors = { contratto: "bg-orange-100 text-orange-600" };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="advanced-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <span className="bg-gradient-to-r from-[#006838] to-emerald-600 text-white p-2 rounded-xl">
              <Activity className="w-5 h-5" />
            </span>
            Dashboard Analytics
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Ultimo aggiornamento: {new Date().toLocaleTimeString("it-IT")}</p>
        </div>
      </div>

      {/* Row 1: Market Prices + Main KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        {marketPrices && (
          <>
            <MetricCard title="PUN Elettrico" value={`€${marketPrices.pun?.value?.toFixed(2) || '—'}`}
              subtitle={marketPrices.pun?.date} icon={Zap} color="text-purple-700" borderColor="border-l-purple-500" />
            <MetricCard title="PSV Gas" value={`€${marketPrices.psv?.value?.toFixed(2) || '—'}`}
              subtitle={`≈ €${(marketPrices.psv?.value / 10.6)?.toFixed(3) || '—'}/Smc`} icon={Flame} color="text-orange-600" borderColor="border-l-orange-500" />
          </>
        )}
        <MetricCard title="Utenti" value={stats.utenti?.totale || 0}
          subtitle={`+${stats.utenti?.nuovi_settimana || 0} settimana`} trend="up" icon={Users} color="text-blue-600" borderColor="border-l-blue-500" />
        <MetricCard title="Analisi" value={stats.analisi?.totale || 0}
          subtitle={`+${stats.analisi?.nuove_settimana || 0} settimana`} trend="up" icon={FileText} color="text-emerald-600" borderColor="border-l-emerald-500" />
        <MetricCard title="Risparmio Tot." value={`€${(stats.risparmio?.totale_generato || 0).toFixed(0)}`}
          subtitle={`Media €${stats.risparmio?.medio_per_analisi || 0}/utente`} icon={Euro} color="text-[#006838]" borderColor="border-l-[#006838]" />
        <MetricCard title="Contratti" value={stats.contratti?.totale || 0}
          subtitle={`${stats.contratti?.recenti_mese || 0} questo mese`} icon={FileSignature} color="text-orange-600" borderColor="border-l-orange-400" />
        <MetricCard title="Offerte" value={stats.offerte?.totale || 0}
          subtitle={`${stats.offerte?.luce || 0} Luce | ${stats.offerte?.gas || 0} Gas`} icon={Zap} color="text-yellow-600" borderColor="border-l-yellow-500" />
      </div>

      {/* Row 2: Conversion Funnel */}
      <Card className="mb-5 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-white" data-testid="conversion-funnel">
        <CardContent className="py-4">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Funnel di Conversione</p>
          <div className="flex items-center gap-3">
            <FunnelStep label="Analisi Bollette" value={stats.funnel?.analisi || 0} color="bg-blue-50 text-blue-700" />
            <FunnelStep label="Contratti Generati" value={stats.funnel?.contratti || 0} color="bg-orange-50 text-orange-700" pct={stats.funnel?.conversion_rate || 0} isLast />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {/* Trend Chart */}
        <Card className="lg:col-span-2" data-testid="trend-chart">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-600" /> Trend Analisi (ultimi 6 mesi)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                  <Area type="monotone" dataKey="analisi" stroke={COLORS.green} fill="url(#areaGrad)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card data-testid="activity-feed">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-600" /> Attività Recente
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            {stats.attivita_recente?.length > 0 ? (
              <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
                {stats.attivita_recente.map((a, i) => {
                  const Icon = activityIcons[a.tipo] || FileText;
                  const colorClass = activityColors[a.tipo] || "bg-gray-100 text-gray-600";
                  return (
                    <div key={i} className="flex items-start gap-2.5 pb-2.5 border-b border-gray-100 last:border-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{a.descrizione}</p>
                        <p className="text-[10px] text-gray-400">{a.id}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">Nessuna attività recente</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Rating Distribution */}
        <Card data-testid="rating-chart">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Esiti Analisi</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="h-[160px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratingData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {ratingData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Luce/Gas Distribution */}
        <Card data-testid="tipo-chart">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Distribuzione Luce/Gas</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="h-[160px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Offers */}
        <Card data-testid="top-offers">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Top Offerte Usate</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            {stats.top_offerte?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_offerte.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                    <span className="text-xs truncate flex-1">{o.nome}</span>
                    <span className="text-[10px] font-bold text-gray-500">{o.utilizzi}x</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-6">Nessun dato</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-testid="quick-actions">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="pb-3 space-y-2">
            <Button className="w-full justify-between text-xs h-9" variant="outline" onClick={() => onTabChange?.("smart-ocr")}>
              <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-purple-600" /> Smart OCR</span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </Button>
            <Button className="w-full justify-between text-xs h-9" variant="outline" onClick={() => onTabChange?.("comparazione")}>
              <span className="flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-emerald-600" /> Comparazione</span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </Button>
            <Button className="w-full justify-between text-xs h-9" variant="outline" onClick={() => onTabChange?.("offerte")}>
              <span className="flex items-center gap-2"><Plus className="w-3.5 h-3.5 text-blue-600" /> Nuova Offerta</span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </Button>
            <Button className="w-full justify-between text-xs h-9" variant="outline" onClick={() => onTabChange?.("venditori")}>
              <span className="flex items-center gap-2"><UserCheck className="w-3.5 h-3.5 text-orange-600" /> Venditori</span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </Button>
            <Button className="w-full justify-between text-xs h-9" variant="outline" onClick={() => onTabChange?.("precheck")}>
              <span className="flex items-center gap-2"><Search className="w-3.5 h-3.5 text-teal-600" /> Verifica POD/PDR</span>
              <ArrowUpRight className="w-3 h-3 text-gray-400" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}
